import type { ReactElement } from 'react'
import type { IReport, IBaseReport, IReportedPost, IReportedComment, IReportedProfile } from '~/types/report'

import { ReportTypeBadge } from '~/components/Badges'
import DataTable, { DataTableItem } from '~/components/Tables/DataTable'
import UserCard from '~/components/UserCard'
import { FASTLY_PATH } from '~/const/asset'
import { REPORT_TYPE } from '~/types/report'

import './styles.scss'

interface Props {
  report?: IReport
}

export const ReportedEntity = ({ report }: Props): ReactElement => {
  switch (report?.reported_entity_type) {
    case REPORT_TYPE.POST:
      const {
        data: { description: postDescription, file_id: postFileId, post_id: postPostId, type: postType },
        reason_types: postReasonType,
        status: postStatus,
      } = report as IBaseReport & {
        data: IReportedPost
      }

      return (
        <DataTable className="report-panel-data-table">
          <DataTableItem label="File ID" value={postFileId} />
          <DataTableItem
            copyEnabled={false}
            label="File"
            value={
              postFileId ? (
                <img alt="Post report picture" src={`${FASTLY_PATH}${postFileId}`} />
              ) : (
                <div>No picture provided</div>
              )
            }
          />
          <DataTableItem label="Post ID" value={postPostId || '-'} />
          <DataTableItem label="Description" value={postDescription || '-'} />
          <DataTableItem label="Type" value={postType} />
          <DataTableItem label="Reason" value={postReasonType[0]} />
          <DataTableItem label="Status" value={postStatus} />
        </DataTable>
      )

    case REPORT_TYPE.COMMENT:
      const {
        data: {
          content: commentText,
          parent_post_comment_id: commentParentPostCommentId,
          post_comment_id: commentPostCommentId,
          post_id: commentPostId,
        },
        reason_types: commentReasonType,
        status: commentStatus,
      } = report as IBaseReport & {
        data: IReportedComment
      }

      return (
        <DataTable className="report-panel-data-table">
          <DataTableItem label="Comment ID" value={commentPostCommentId} />
          <DataTableItem copyEnabled={false} label="Comment" value={commentText} />
          <DataTableItem label="Post ID" value={commentPostId || '-'} />
          <DataTableItem label="Parent Comment Post ID" value={commentParentPostCommentId || '-'} />
          <DataTableItem label="Reason" value={commentReasonType.join(', ')} />
          <DataTableItem label="Status" value={commentStatus} />
        </DataTable>
      )

    case REPORT_TYPE.PROFILE:
      const {
        data: {
          bio: profileBio,
          display_name: profileDisplayName,
          nb_followers: profileNbFollowers,
          nb_follows: profileNbFollows,
          profile_id: profileProfileId,
          profile_picture_file_id: profilePictureFileId,
        },
        reason_types: profileReasonType,
        status: profileStatus,
      } = report as IBaseReport & {
        data: IReportedProfile
      }

      return (
        <DataTable className="report-panel-data-table">
          <DataTableItem label="Profile ID" value={profileProfileId} />
          <DataTableItem
            copyEnabled={false}
            label="Picture"
            value={<UserCard name={profileDisplayName} picture={`${FASTLY_PATH}${profilePictureFileId}`} />}
          />
          <DataTableItem
            copyEnabled={false}
            label="Followers / Follows"
            value={
              <>
                <ReportTypeBadge
                  value={`${profileNbFollowers.toString()} follower${profileNbFollowers > 1 ? 's' : ''}`}
                />
                <ReportTypeBadge value={`${profileNbFollows.toString()} follow${profileNbFollows > 1 ? 's' : ''}`} />
              </>
            }
          />
          <DataTableItem label="Bio" value={profileBio} />
          <DataTableItem label="Reason" value={profileReasonType.join(', ')} />
          <DataTableItem label="Status" value={profileStatus} />
        </DataTable>
      )

    default:
      return <div>No data</div>
  }
}
